import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEstimates } from "@/hooks/useEstimates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, FileText, Copy, Trash2 } from "lucide-react";
import { format } from "date-fns";

const Estimates = () => {
  const navigate = useNavigate();
  const { estimates, isLoading, deleteEstimate } = useEstimates();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredEstimates = estimates?.filter((estimate) => {
    const matchesSearch =
      estimate.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      estimate.estimate_number.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || estimate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "secondary";
      case "sent":
        return "default";
      case "accepted":
        return "default";
      case "declined":
        return "destructive";
      default:
        return "secondary";
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Estimates</h1>
          <p className="text-muted-foreground">
            Create and manage customer estimates
          </p>
        </div>
        <Button onClick={() => navigate("/admin/estimates/builder")}>
          <Plus className="mr-2 h-4 w-4" />
          New Estimate
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer or estimate number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estimate #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Valid Until</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  Loading estimates...
                </TableCell>
              </TableRow>
            ) : filteredEstimates?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No estimates found
                </TableCell>
              </TableRow>
            ) : (
              filteredEstimates?.map((estimate) => (
                <TableRow key={estimate.id}>
                  <TableCell className="font-medium">
                    {estimate.estimate_number}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{estimate.customer_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {estimate.customer_phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {estimate.city}, {estimate.county}
                  </TableCell>
                  <TableCell className="font-medium">
                    ${estimate.total.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusColor(estimate.status)}>
                      {estimate.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {format(new Date(estimate.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {estimate.valid_until
                      ? format(new Date(estimate.valid_until), "MMM d, yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          navigate(`/admin/estimates/builder/${estimate.id}`)
                        }
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteEstimate.mutate(estimate.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Estimates;
